import React from 'react';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'localization';
import type { StatusValue } from 'types/spherePoints';
import type { VelocityBucketKey } from 'types/sphereMetrics';
import CheckInFormContent from './CheckInFormContent';
import './StatusCheckInDialogMolecule.scss';

export type { StatusValue } from 'types/spherePoints';

export interface CheckInPeriodInfo {
  /** Due date for the period being checked in (ISO 8601) */
  dueAt?: string;
  /** Period start date */
  periodStart?: string;
  /** Period end date */
  periodEnd?: string;
  /** Total number of periods in the squashed group */
  groupCount?: number;
}

export interface StatusCheckInDialogProps {
  visible: boolean;
  factorName?: string;
  /** Period info to display in the dialog */
  periodInfo?: CheckInPeriodInfo;
  /** Pre-select the status dropdown with the factor's current status */
  defaultStatus?: StatusValue;
  commentRequired?: boolean;
  minCommentLength?: number;
  loading?: boolean;
  velocityScore?: number;
  velocityBucket?: VelocityBucketKey;
  onSubmit: (statusValue: StatusValue, comment?: string) => void;
  onCancel: () => void;
}

function formatPeriodDate(isoDate: string, locale: string): string {
  try {
    return new Date(isoDate).toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

const StatusCheckInDialogMolecule: React.FC<StatusCheckInDialogProps> = ({
  visible,
  factorName,
  periodInfo,
  defaultStatus,
  commentRequired = false,
  minCommentLength = 0,
  loading = false,
  velocityScore,
  velocityBucket,
  onSubmit,
  onCancel,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en-US';

  return (
    <Dialog
      header={
        factorName
          ? t('statusPopover.checkIn.dialogTitleWithFactor', 'Check-In: {{factorName}}', {
              factorName,
            })
          : t('statusPopover.checkIn.dialogTitle', 'Status Check-In')
      }
      visible={visible}
      onHide={onCancel}
      className="status-checkin-dialog"
      modal
      closable={!loading}
    >
      {periodInfo?.dueAt && (
        <div className="checkin-period-info">
          <span className="checkin-period-info__due">
            <i className="pi pi-calendar" />
            {t('statusPopover.checkIn.periodDue', {
              date: formatPeriodDate(periodInfo.dueAt, locale),
            })}
          </span>
          {periodInfo.periodStart && periodInfo.periodEnd && (
            <span className="checkin-period-info__range">
              {formatPeriodDate(periodInfo.periodStart, locale)} – {formatPeriodDate(periodInfo.periodEnd, locale)}
            </span>
          )}
          {(periodInfo.groupCount ?? 0) > 1 && (
            <span className="checkin-period-info__group">
              {t('statusPopover.checkIn.periodGroupCount', {
                count: periodInfo.groupCount,
              })}
            </span>
          )}
        </div>
      )}
      {/* Key forces re-mount (state reset) when dialog opens */}
      <CheckInFormContent
        key={visible ? 'open' : 'closed'}
        defaultStatus={defaultStatus}
        commentRequired={commentRequired}
        minCommentLength={minCommentLength}
        velocityScore={velocityScore}
        velocityBucket={velocityBucket}
        isSubmitting={loading}
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
    </Dialog>
  );
};

export default StatusCheckInDialogMolecule;
